import { DialogBackdropCloser } from "./backdrop-dialog.37ba46c3e019.js";
import CustomAmplitudeClient from "./custom-amplitude-client.b75422c9d833.js";
import {
  OpenInsuranceSelectorEventType,
  InsuranceSelectorPlanChosenEvent,
  InsuranceSelectorDialogPlanSelectedEventType,
  InsuranceSelectorDialogPlanResetEventType,
  InsuranceSelectorDialogPlanSelectedWithCostShareEventType,
  InsuranceSelectorDialogMemberIdConfirmedEventType,
  InsuranceSelectorDialogNoPlanSelectedEventType,
} from "./insurance-selector-events.382c4178547f.js";

/**
 * An <insurance-selector-dialog>.
 *
 * @emits: tqc:insurance-selector:changed
 * @listens: tqc:insurance-selector:open
 */
class InsuranceSelectorDialog extends DialogBackdropCloser {
  constructor() {
    super();

    this.parent = this.getRootNode() || document;
    this.config = JSON.parse(
      this.parent.querySelector("#page-config").textContent,
    );
    if (!this.config) throw new Error("unable to load page config");
  }

  connectedCallback() {
    super.connectedCallback();

    this.bindElements();

    this.parent.addEventListener(
      OpenInsuranceSelectorEventType,
      this.handleOpenEvent,
    );
    this.dialog.addEventListener(
      InsuranceSelectorDialogPlanSelectedEventType,
      this.handleClose,
    );
    this.dialog.addEventListener(
      InsuranceSelectorDialogPlanSelectedWithCostShareEventType,
      this.savePlanDetails,
    );
    this.dialog.addEventListener(
      InsuranceSelectorDialogNoPlanSelectedEventType,
      this.handleClose,
    );
    this.dialog.addEventListener(
      InsuranceSelectorDialogMemberIdConfirmedEventType,
      this.handleClose,
    );
    this.dialog.addEventListener(
      InsuranceSelectorDialogPlanResetEventType,
      this.handleClose,
    );
    this.addEventListener("htmx:afterSettle", this.bindElements);
    this.amplitudeClient = new CustomAmplitudeClient(this.config.analyticsUrl);
  }

  bindElements = () => {
    let modalIndicator = this.dialog.querySelector(".tqc-modal-indicator");
    let dialogViewport = this.dialog.querySelector(".tqc-dialog-viewport");

    this.elements = {
      form: this.dialog.querySelector("form.tqc-modal-step"),
      dialogViewport: dialogViewport,
      dialogClose: this.dialog.querySelectorAll(
        ".tqc-close-button:not(div.tqc-modal-indicator .tqc-close-button)",
      ),
      dialogBack: this.dialog.querySelectorAll(
        ".tqc-modal-back:not(div.tqc-modal-indicator .tqc-modal-back, #bcbs-plans .tqc-modal-back)",
      ),
      dialogAriaLabels: this.dialog.querySelectorAll(".tqc-modal-label"),
      modalIndicator: modalIndicator,
      dialogCloseIndicator:
        modalIndicator?.querySelectorAll(".tqc-close-button") || [],
      dialogBackIndicator:
        modalIndicator?.querySelectorAll(".tqc-modal-back") || [],
      pollingStep: dialogViewport?.querySelector(".tqc-polling-step"),
      BCBSSubNav: this.dialog.querySelector("#bcbs-subnav"),
      nonBCBSPlans: this.dialog.querySelector("#non-bcbs-plans"),
      BCBSPlans: this.dialog.querySelector("#bcbs-plans"),
      dialogBackBCBSPlans: this.dialog.querySelectorAll(
        "#bcbs-plans .tqc-modal-back",
      ),
    };

    this.elements.dialogClose.forEach((closeButton) => {
      closeButton.addEventListener("click", this.handleCloseButton);
    });
    this.elements.dialogBack.forEach((backButton) => {
      backButton.addEventListener("click", this.handleBackButton);
    });

    this.elements.dialogCloseIndicator.forEach((closeButton) => {
      closeButton.addEventListener("click", this.handleIndicatorCloseButton);
    });
    this.elements.dialogBackIndicator.forEach((backButton) => {
      backButton.addEventListener("click", this.handleIndicatorBackButton);
    });
    this.elements.BCBSSubNav?.addEventListener("click", this.handleBCBSSubNav);
    this.elements.dialogBackBCBSPlans.forEach((backButton) => {
      backButton.addEventListener("click", this.handleBCBSPlansBackButton);
    });

    this.dialog.addEventListener("keydown", this.handleListItemKeydown);

    const visibleStep = this.dialog.querySelector(
      ".tqc-modal-step:not(.pv-hide)",
    );
    const initialFocusElement = visibleStep?.querySelector(
      "[data-initial-focus]",
    );
    if (initialFocusElement) {
      initialFocusElement.focus();
    }

    this.setDialogAriaLabelledBy();
  };

  handleListItemKeydown = (e) => {
    if (e.key !== "Enter") return;
    const li = e.target.closest("li[role='option']");
    if (!li) return;

    const radio = li.querySelector("input[type='radio']");
    if (radio) {
      radio.checked = true;
      radio.dispatchEvent(new Event("change", { bubbles: true }));
      return;
    }

    // For non-radio items like BCBS subnav link
    const link = li.querySelector("a");
    if (link) {
      this.handleBCBSSubNav(e);
    }
  };

  disconnectedCallback() {
    this.parent.removeEventListener(
      OpenInsuranceSelectorEventType,
      this.handleOpenEvent,
    );
  }

  handleOpenEvent = (e) => {
    if (this.elements.form?.plan?.value) {
      this.amplitudeClient.trackStartBenefitsInfo(e.detail?.navMethod);
    }
    if (e.scroll) {
      this.scrollIntoView();
    }
    this.open();
  };

  handleBCBSSubNav = (e) => {
    e.preventDefault();
    this.elements.BCBSPlans.classList.remove("pv-hide");
    this.elements.nonBCBSPlans.classList.add("pv-hide");

    this.setDialogAriaLabelledBy();
  };

  handleBCBSPlansBackButton = (e) => {
    e.preventDefault();
    this.elements.BCBSPlans.classList.add("pv-hide");
    this.elements.nonBCBSPlans.classList.remove("pv-hide");

    this.setDialogAriaLabelledBy();
  };

  handleIndicatorBackButton = (e) => {
    e.preventDefault();

    this.cancelPolling();

    this.elements.form.classList.remove("pv-hide");
    this.setDialogAriaLabelledBy();
  };

  handleBackButton = (e) => {
    e.preventDefault();

    this.elements.form.classList.remove("pv-hide");
    e.target.closest(".tqc-modal-step").classList.add("pv-hide");

    this.setDialogAriaLabelledBy();
  };

  handleClose = () => {
    this.close();
  };

  handleIndicatorCloseButton = (e) => {
    e.preventDefault();

    this.cancelPolling();

    this.close();
  };

  handleCloseButton = (e) => {
    e.preventDefault();

    this.close();

    if (this.planDetails) {
      this.dispatchEvent(
        new InsuranceSelectorPlanChosenEvent(this.planDetails),
      );
    }
  };

  savePlanDetails = (e) => {
    this.planDetails = e.detail;
  };

  open() {
    this.dialog.showModal();
  }

  cancelPolling() {
    // Remove loading indicators:
    // - is-polling (ours, prevents flicker between poll intervals) and
    // - htmx-request
    //    (normally HTMX-managed, but if user cancels mid-swap,
    //      HTMX's cleanup never runs, since there's no longer a request
    //      to abort)
    //
    // Re-query is needed in case we're on a stale element post-swap
    const viewport = this.dialog.querySelector(".tqc-dialog-viewport");
    viewport?.classList.remove("is-polling", "htmx-request");
    if (this.elements.pollingStep) {
      htmx.trigger(this.elements.pollingStep, "htmx:abort");
      this.elements.pollingStep.remove();
    }
  }

  setDialogAriaLabelledBy = () => {
    // Because the contents of the modal changes, the aria-labelledby needs to be updated to an eligible element.
    // Find the first possible aria label that is not within a hidden parent
    const visibleLabels = Array.from(this.elements.dialogAriaLabels).find(
      (el) => !el.closest(".pv-hide"),
    );
    const labelId = visibleLabels?.id;
    this.dialog.setAttribute("aria-labelledby", labelId);
  };
}

customElements.define("insurance-selector-dialog", InsuranceSelectorDialog);
